import { Autocomplete, Box, TextField } from '@mui/material';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useGetAccountById } from '../../services/react-query/accounts';
import { useGetAllContactTemplates } from '../../services/react-query/contact_templates';
import { useGetContactById } from '../../services/react-query/contacts/useGetContactById';
import { Contact } from '../../types/Contact';
import ContactBasicInfoForm from './ContactBasicInfoForm';

type TemplateDisplay = {
  name: string;
  uuid: string | null;
};

interface Props {
  accountUuid: string;
  contact: Contact;
  setContact: (c: Contact) => void;
}

export default function CreateContactForm({ accountUuid, contact, setContact }: Props) {
  const blankTemplate = { name: 'Blank Template', uuid: null };
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateDisplay>(blankTemplate);

  const { data: contactTemplatesData } = useGetAllContactTemplates({ params: { per_page: 0 } });
  const contactTemplates = contactTemplatesData?.data ?? [];

  const { data: accountDataFromContact } = useGetAccountById({
    uuid: accountUuid,
    enabled: !!accountUuid,
  });

  const displayOptions = useMemo(() => {
    return [
      blankTemplate,
      ..._.map(contactTemplates, (t) => ({
        name: t.name,
        uuid: t.uuid,
      })),
    ];
  }, [contactTemplates]);

  const { data: contactData } = useGetContactById({
    uuid: 'new',
    template: selectedTemplate.uuid ?? undefined,
  });

  useEffect(() => {
    if (contactData) {
      setContact({
        ...contactData,
        account_uuid: accountUuid,
      });
    }
  }, [contactData, accountUuid, setContact]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
      <Autocomplete<TemplateDisplay>
        options={displayOptions}
        value={selectedTemplate}
        onChange={(_, val) => setSelectedTemplate(val!)}
        getOptionLabel={(o) => o.name}
        renderInput={(params) => <TextField {...params} label="Template" />}
      />
      <ContactBasicInfoForm contact={contact} setContact={setContact} account={accountDataFromContact} />
    </Box>
  );
}
